// Comment types — unified from frontend and backend.
//
// Schemas are authoritative; the TS types are derived via z.infer<>. Two shapes
// intentionally stay TS interfaces:
//   - Comment is self-referential (`children?: Comment[]`), so it keeps its
//     interface and a companion z.lazy() CommentSchema for runtime validation.
//   - CommentResponse extends the generic ApiResponse<Comment> from ./common,
//     which Zod can't cleanly express without duplicating that module.
// Consumers can import the *Schema values for runtime validation of API
// responses, form input, or Firestore reads.

import { z } from 'zod';
import type { ApiResponse } from './common';
import { ReportStatusSchema } from './quiz';
import { VoteRequestBodySchema, type VoteType, VoteTypeSchema } from './voting';

/**
 * Sort order for API requests
 */
export const SortOrderSchema = z.enum(['asc', 'desc']);
export type SortOrder = z.infer<typeof SortOrderSchema>;

/**
 * Standard comment interface used across all applications
 * Represents a user comment on a question with voting, threading, and metadata
 *
 * Stays a TS interface because it is recursive (`children?: Comment[]`); the
 * companion {@link CommentSchema} models it at runtime via z.lazy().
 */
export interface Comment {
  // Core identification
  id: string;
  questionId: string;
  userId: string;

  // Content
  text: string;

  // User display data (denormalized for performance)
  username: string | null;
  userProfilePic: string | null;

  // Threading and hierarchy
  parentId: string | null;
  depth?: number;
  children?: Comment[]; // Populated for tree structure
  childCount?: number; // Number of direct replies

  // Voting and engagement
  upvotes?: number;
  downvotes?: number;
  score?: number; // upvotes - downvotes
  /** The requesting user's own vote. Present only on an authenticated read; never stored. */
  userVote?: VoteType;

  // Metadata and moderation
  isPinned?: boolean;
  isDeleted?: boolean;
  /** Hidden from the feed pending moderation review — set when reported, cleared on restore. */
  isHidden?: boolean;

  // Timestamps (ISO strings)
  createdAt: string;
  updatedAt?: string;
}

/**
 * Runtime schema for {@link Comment}. Self-referential, so it is annotated with
 * z.ZodType<Comment> and defers the recursion through z.lazy().
 */
export const CommentSchema: z.ZodType<Comment> = z.lazy(() =>
  z.object({
    // Core identification
    id: z.string(),
    questionId: z.string(),
    userId: z.string(),

    // Content
    text: z.string(),

    // User display data (denormalized for performance)
    username: z.string().nullable(),
    userProfilePic: z.string().nullable(),

    // Threading and hierarchy
    parentId: z.string().nullable(),
    depth: z.number().optional(),
    children: z.array(CommentSchema).optional(), // Populated for tree structure
    childCount: z.number().optional(), // Number of direct replies

    // Voting and engagement
    upvotes: z.number().optional(),
    downvotes: z.number().optional(),
    score: z.number().optional(), // upvotes - downvotes
    /** The requesting user's own vote. Present only on an authenticated read; never stored. */
    userVote: VoteTypeSchema.optional(),

    // Metadata and moderation
    isPinned: z.boolean().optional(),
    isDeleted: z.boolean().optional(),
    isHidden: z.boolean().optional(),

    // Timestamps (ISO strings)
    createdAt: z.string(),
    updatedAt: z.string().optional(),
  }),
);

/**
 * Request payload for creating a new comment
 */
export const CreateCommentRequestSchema = z.object({
  /** ID of the question being commented on */
  questionId: z.string(),
  /** The comment text content */
  text: z.string(),
  /** ID of the parent comment for replies, or null for top-level */
  parentId: z.string().nullable(),
});
export type CreateCommentRequest = z.infer<typeof CreateCommentRequestSchema>;

/**
 * Request payload for updating an existing comment. Text-only — the backend
 * edit path never supported pinning, and comment pinning isn't a product
 * feature, so the phantom `isPinned` was dropped.
 */
export const UpdateCommentRequestSchema = z.object({
  /** New comment text content */
  text: z.string().optional(),
});
export type UpdateCommentRequest = z.infer<typeof UpdateCommentRequestSchema>;

/**
 * Request payload for voting on a comment — the same body as a question vote, so the generic
 * client `voteOnItem` serves both. (This used to declare `{ commentId, voteType }`, which nothing
 * sent: the comment id travels in the URL and the client posts `vote`.)
 */
export const CommentVoteRequestSchema = VoteRequestBodySchema;
export type CommentVoteRequest = z.infer<typeof CommentVoteRequestSchema>;

/** The orderings the comments list can be served in — each one is backed by a composite index. */
export const CommentSortBySchema = z.enum(['createdAt', 'score']);
export type CommentSortBy = z.infer<typeof CommentSortBySchema>;

/**
 * Response for comment operations
 *
 * Stays a TS interface because it extends the generic ApiResponse<Comment>.
 */
export interface CommentResponse extends ApiResponse<Comment> {
  comment?: Comment;
}

/**
 * Options for fetching comments with pagination and threading support
 */
export const GetCommentsOptionsSchema = z.object({
  /** null for top-level, string ID for replies to a specific comment */
  parentId: z.string().nullable().optional(),
  /** Number of comments to fetch per page */
  limit: z.number().optional(),
  /** Document ID to start after for pagination */
  lastVisibleDocId: z.string().optional(),
  /** Field to sort by */
  sortBy: CommentSortBySchema.optional(),
  /** Sort direction */
  order: SortOrderSchema.optional(),
});
export type GetCommentsOptions = z.infer<typeof GetCommentsOptionsSchema>;

/**
 * Response structure for comment fetching API
 */
export const GetCommentsResponseSchema = z.object({
  /** Array of comments (with nested children if populated) */
  data: z.array(CommentSchema),
  /** Document ID for the last item in the current page */
  lastDocId: z.string().optional(),
  /** Whether more comments are available */
  hasMore: z.boolean().optional(),
  /** Total number of comments matching the query */
  total: z.number().optional(),
});
export type GetCommentsResponse = z.infer<typeof GetCommentsResponseSchema>;

// ---------------------------------------------------------------------------
// Comment reports (moderation)
//
// A user reports a comment → it is hidden pending review → an admin restores or
// removes it. The report denormalizes the offending author and a text snapshot
// at report time, so the record stays meaningful after the comment is soft-
// removed and remains pinned to the responsible user. Mirrors QuestionReport
// (see ./quiz). See docs/comment-moderation-plan.md.
// ---------------------------------------------------------------------------

/**
 * Reasons a user can report a comment. Distinct from the question ReportReason —
 * these target user-authored discussion and lean toward what the write-time
 * profanity/AI gate does not catch (e.g. medical misinformation).
 */
export const CommentReportReasonSchema = z.enum([
  'misinformation',
  'harassment',
  'spam',
  'inappropriate',
  'off_topic',
  'other',
]);
export type CommentReportReason = z.infer<typeof CommentReportReasonSchema>;

/**
 * A stored comment report. `reportedUserId` / `reportedUsername` / `commentText`
 * are denormalized at report time so the report stays meaningful after the
 * comment is removed. Deduped one-per-(comment, reporter) at the model layer.
 */
export const CommentReportSchema = z.object({
  id: z.string(),
  commentId: z.string(),
  questionId: z.string(),
  reporterId: z.string(),
  /** The reported comment's author, captured at report time. */
  reportedUserId: z.string(),
  reportedUsername: z.string().nullable(),
  /** Snapshot of the comment body at report time. */
  commentText: z.string(),
  reason: CommentReportReasonSchema,
  description: z.string().optional(),
  status: ReportStatusSchema,
  createdAt: z.string(),
  // Populated for admin display.
  reporterEmail: z.string().optional(),
  reporterDisplayName: z.string().optional(),
  resolvedBy: z.string().optional(),
  resolvedAt: z.string().optional(),
});
export type CommentReport = z.infer<typeof CommentReportSchema>;

/**
 * Request body for reporting a comment. The commentId comes from the route path.
 */
export const CreateCommentReportRequestSchema = z.object({
  reason: CommentReportReasonSchema,
  description: z.string().optional(),
});
export type CreateCommentReportRequest = z.infer<typeof CreateCommentReportRequestSchema>;
